import { useEffect, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, MapPin, ChevronRight, Heart, Loader2, ImagePlus } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { useLead } from "@/components/public/LeadDialog";
import { memberApi, getMemberToken } from "@/lib/member";
import { Txt, EditImage, ItemTools, AddBtn } from "@/components/public/AboutSections";
import { Picture } from "@/components/public/Picture";

export const LOCATIONS_DEFAULTS = {
  hero: {
    eyebrow: "Our Locations",
    title: "Prime Business Locations in Noida",
    subtitle: "Premium CoworkZen centres in the most connected business districts, designed for productivity and growth.",
  },
  sections: [],
};

export function withLocationDefaults(content) {
  const c = content || {};
  return {
    ...LOCATIONS_DEFAULTS,
    ...c,
    hero: { ...LOCATIONS_DEFAULTS.hero, ...(c.hero || {}) },
    sections: Array.isArray(c.sections) ? c.sections : [],
  };
}

function Breadcrumbs({ items }) {
  return (
    <nav className="container-cz pt-6 flex items-center gap-1.5 text-sm text-slate-500" aria-label="Breadcrumb">
      {items.map((it, i) => (
        <span key={i} className="flex items-center gap-1.5">
          {i > 0 && <ChevronRight className="w-3.5 h-3.5 text-slate-300" />}
          {it.to ? <Link to={it.to} className="hover:text-primary">{it.label}</Link> : <span className="text-slate-900 font-medium">{it.label}</span>}
        </span>
      ))}
    </nav>
  );
}

function useSaved() {
  const [slugs, setSlugs] = useState(() => new Set());
  const navigate = useNavigate();
  useEffect(() => {
    if (!getMemberToken()) return;
    memberApi.me()
      .then((r) => setSlugs(new Set((r.data.saved_locations || []).map((s) => s.slug || s))))
      .catch(() => {});
  }, []);
  const toggle = async (payload) => {
    if (!getMemberToken()) {
      toast.info("Sign in to save your favourites");
      navigate("/account");
      return;
    }
    try {
      const { data } = await memberApi.toggleSaved(payload);
      setSlugs(new Set((data.saved_locations || []).map((s) => s.slug || s)));
    } catch {
      toast.error("Could not update favourites");
    }
  };
  return { slugs, toggle };
}

function SaveHeart({ saved, onClick, testid }) {
  return (
    <span
      role="button"
      tabIndex={0}
      data-testid={testid}
      onClick={(e) => { e.stopPropagation(); e.preventDefault(); onClick(); }}
      onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); onClick(); } }}
      aria-label={saved ? "Remove from favourites" : "Save to favourites"}
      className="absolute top-3 right-3 z-10 w-9 h-9 rounded-full bg-white/90 backdrop-blur flex items-center justify-center shadow-sm hover:scale-110 transition-transform cursor-pointer"
    >
      <Heart className={`w-[18px] h-[18px] ${saved ? "fill-rose-500 text-rose-500" : "text-slate-600"}`} />
    </span>
  );
}

function CardImage({ loc, edit, onReplaced }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data } = await api.post("/admin/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.put(`/admin/locations/${loc.id}/image`, { image: data.url });
      onReplaced(loc.id, data.url);
      toast.success("Location photo updated");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); e.target.value = ""; }
  };
  return (
    <div className="aspect-[16/10] overflow-hidden relative group/img">
      <Picture src={loc.image} alt={`${loc.name}, ${loc.sector}`} loading="lazy" width={700} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
      {edit && (
        <>
          <button type="button" onClick={() => ref.current?.click()} disabled={busy} data-testid={`loc-edit-image-${loc.slug}`}
            className="absolute inset-0 z-20 flex items-center justify-center gap-2 bg-slate-900/55 text-white text-sm font-medium opacity-0 group-hover/img:opacity-100 transition-opacity">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImagePlus className="w-4 h-4" />} {busy ? "Uploading…" : "Replace image"}
          </button>
          <input ref={ref} type="file" accept="image/png,image/jpeg,image/webp,image/gif,image/svg+xml" hidden onChange={pick} />
        </>
      )}
    </div>
  );
}

export default function LocationsSections({ data, edit }) {
  const [items, setItems] = useState([]);
  const navigate = useNavigate();
  const { openLead } = useLead();
  const { slugs, toggle } = useSaved();
  useEffect(() => {
    api.get("/public/locations").then((r) => setItems(r.data)).catch(() => {});
  }, []);
  const onReplaced = (id, url) => setItems((prev) => prev.map((x) => (x.id === id ? { ...x, image: url } : x)));
  const hero = data.hero || {};
  const sections = data.sections || [];
  return (
    <div data-testid="locations-page-body">
      <Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "Locations" }]} />

      <div className="container-cz pt-8 pb-10">
        <Txt edit={edit} path="hero.eyebrow" value={hero.eyebrow} as="p" className="eyebrow mb-3 inline-block" placeholder="Eyebrow" />
        <Txt edit={edit} path="hero.title" value={hero.title} as="h1" className="block font-display text-4xl sm:text-5xl font-extrabold text-slate-900 max-w-3xl" placeholder="Page title" />
        <Txt edit={edit} path="hero.subtitle" value={hero.subtitle} as="p" className="text-lg text-slate-600 mt-4 max-w-2xl" placeholder="Subtitle" />
      </div>

      <div className="container-cz pb-16 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {items.map((l) => (
          <div key={l.id} className="group card-cz overflow-hidden hover:shadow-cz transition-all relative" data-testid={`loc-list-${l.slug}`}>
            <SaveHeart testid={`loc-save-${l.slug}`} saved={slugs.has(l.slug)}
              onClick={() => toggle({ slug: l.slug, name: l.name, kind: "location" })} />
            <CardImage loc={l} edit={edit} onReplaced={onReplaced} />
            <div className="p-5">
              <h2 className="font-display text-xl font-bold text-slate-900">{l.name}</h2>
              <p className="text-sm text-primary font-medium mt-0.5 flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {l.sector}</p>
              <p className="text-sm text-slate-500 mt-2.5">{l.description}</p>
              <div className="flex gap-2.5 mt-5">
                <Button onClick={() => navigate(`/locations/${l.slug}`)} className="rounded-full h-10 px-4 gap-1.5 flex-1 text-sm">View Space <ArrowRight className="w-3.5 h-3.5" /></Button>
                <Button onClick={() => openLead("tour", { location: l.slug, city: (l.sector || "").split(",").pop().trim() })} variant="outline" className="rounded-full h-10 px-4 flex-1 text-sm border-slate-300">Book a Tour</Button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {(sections.length > 0 || edit) && (
        <div className="container-cz pb-20 space-y-10" data-testid="locations-content-sections">
          {sections.map((s, i) => {
            const showImg = s.image || edit;
            return (
              <div key={i} className="relative group/item">
                {edit && <ItemTools edit={edit} path="sections" index={i} count={sections.length} />}
                <div className={showImg ? "grid md:grid-cols-2 gap-6 lg:gap-10 items-center" : ""}>
                  {showImg && (
                    <div className={i % 2 === 1 ? "md:order-2" : ""}>
                      <EditImage edit={edit} path={`sections.${i}.image`} value={s.image}
                        wrapClass="rounded-2xl overflow-hidden bg-slate-100 aspect-[16/10]"
                        imgClass="w-full h-full object-cover" placeholderLabel="section image" />
                    </div>
                  )}
                  <div className={showImg ? "" : "max-w-3xl"}>
                    <Txt edit={edit} path={`sections.${i}.heading`} value={s.heading} as="h2" className="block font-display text-2xl sm:text-3xl font-bold text-slate-900" placeholder="Section heading" />
                    <Txt edit={edit} path={`sections.${i}.body`} value={s.body} as="p" className="text-slate-600 mt-3 leading-relaxed whitespace-pre-line" placeholder="Write your content here…" />
                  </div>
                </div>
              </div>
            );
          })}
          {edit && <AddBtn edit={edit} path="sections" blank={{ heading: "New section heading", body: "Write your content here…", image: "" }} label="Add content section" />}
        </div>
      )}
    </div>
  );
}
